import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

import { SaveMigracion } from "../components/save-migracion/SaveMigracion";
import { TableMigracion } from "../components/table-migracion/TableMigracion";
import { ParameterFormMigracion } from "../components/parameter-form/ParameterFormMigracion";
import { ConfigSummaryCard } from "../components/config-summary/ConfigSummaryCard";
import { TicketsModalMigracion } from "../components/tickets-modal/TicketsModalMigracion";

import { TicketsModalFidelizacion } from "../../fidelizacion/components/tickets-modal";
import { ParameterFormFidelizacion } from "../../fidelizacion/components/parameter-form/ParameterFormMigracion";

import { NotFoundCandidatesModal } from "../../components/notfound-modal/NotFoundCandidatesModal";
import { InternalSearchCandidates } from "../../components/internal-search";
import type {
  ContratoCampanasMigracionFiltroRequest,
  ContratoCampanasMigracionResponse,
} from "@/shared/actions/campanas/migracion.actions";
import type {
  ContratoCampanasFiltroRequest,
  ContratoCampanasFiltroResponse,
} from "@/shared/actions/campanas/fidelizacion.actions";
import {
  getCampaignTraceability,
  type CampaignTraceabilityResponse,
  type TraceabilityAction,
  type TraceabilityCustomerResponse,
  type TraceabilityStatus,
} from "@/shared/actions/campanas/traceability.actions";
import { useAvailableTicketCandidates } from "../../hooks/useAvailableTicketCandidates";

type CampaignType = "MIGRATION" | "FIDELIZATION";
type CampaignTypeValue = "" | CampaignType;

type DataSourceType = "" | "CSV" | "INTERNAL_SEARCH";
type TraceabilityActionValue = "" | TraceabilityAction;
type TraceabilityStatusValue = "" | TraceabilityStatus;

const traceabilityActionLabels: Record<TraceabilityAction, string> = {
  SEARCH: "Búsqueda",
  CREATE_TICKETS: "Crear tickets",
  SEND_EMAIL: "Enviar email",
};

const traceabilityStatusLabels: Record<TraceabilityStatus, string> = {
  SUCCESS: "Correcto",
  WARNING: "Parcial",
  ERROR: "Error",
};

const traceabilityStatusColors: Record<
  TraceabilityStatus,
  "success" | "warning" | "error"
> = {
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error",
};

const traceabilityActionColors: Record<
  TraceabilityAction,
  "info" | "primary" | "secondary"
> = {
  SEARCH: "info",
  CREATE_TICKETS: "primary",
  SEND_EMAIL: "secondary",
};

const getCampaignLabel = (value: CampaignTypeValue) => {
  if (value === "MIGRATION") return "Migración";
  if (value === "FIDELIZATION") return "Fidelización";
  return "Sin campaña";
};

const getDataSourceLabel = (value: DataSourceType) => {
  if (value === "CSV") return "Carga CSV/XLSX";
  if (value === "INTERNAL_SEARCH") return "Búsqueda interna";
  return "Sin fuente";
};

const countJsonLikeList = (value?: string | null) => {
  if (!value) return "-";

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.length;
  } catch {
    // Algunos registros pueden venir como texto separado por comas.
  }

  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean).length;
};

const getTraceabilityCustomers = (
  row: CampaignTraceabilityResponse,
): TraceabilityCustomerResponse[] => {
  if (Array.isArray(row.customers)) {
    return row.customers;
  }

  if (!row.selectedCandidates) {
    return [];
  }

  try {
    const parsed = JSON.parse(row.selectedCandidates);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(
      (candidate) =>
        candidate &&
        typeof candidate === "object" &&
        !Array.isArray(candidate),
    );
  } catch {
    return [];
  }
};

const summarizeCustomers = (
  row: CampaignTraceabilityResponse,
  field: "name" | "identification",
) => {
  const values = getTraceabilityCustomers(row)
    .map((candidate) => {
      if (field === "identification") {
        return candidate.identification;
      }

      return (
        candidate.razonSocial ||
        candidate.customerName ||
        (candidate as any).razon_social
      );
    })
    .filter((value): value is string => !!value);

  if (values.length === 0) {
    return "-";
  }

  const uniqueValues = Array.from(new Set(values));
  const visibleValues = uniqueValues.slice(0, 2).join(", ");

  if (uniqueValues.length <= 2) {
    return visibleValues;
  }

  return `${visibleValues} +${uniqueValues.length - 2}`;
};

const formatTraceabilityDate = (value?: string | null) => {
  if (!value) return "-";

  return value.slice(0, 10);
};

export const CampanasPage = () => {
  const [campaignType, setCampaignType] = useState<CampaignTypeValue>("");
  const [dataSource, setDataSource] = useState<DataSourceType>("");

  const [openParameterDrawer, setOpenParameterDrawer] = useState(false);
  const [openMigrationTicketsModal, setOpenMigrationTicketsModal] =
    useState(false);
  const [openFidelizationTicketsModal, setOpenFidelizationTicketsModal] =
    useState(false);
  const [openNotFoundModal, setOpenNotFoundModal] = useState(false);

  const [migrationSearchCandidates, setMigrationSearchCandidates] =
    useState<ContratoCampanasMigracionResponse[]>([]);
  const [migrationReportFilters, setMigrationReportFilters] =
    useState<ContratoCampanasMigracionFiltroRequest | null>(null);
  const [fidelizationReportFilters, setFidelizationReportFilters] =
    useState<ContratoCampanasFiltroRequest | null>(null);
  const [fidelizationSearchCandidates, setFidelizationSearchCandidates] =
    useState<ContratoCampanasFiltroResponse[]>([]);
  const [traceabilityRows, setTraceabilityRows] = useState<
    CampaignTraceabilityResponse[]
  >([]);
  const [traceabilityTotal, setTraceabilityTotal] = useState(0);
  const [traceabilityPage, setTraceabilityPage] = useState(0);
  const [traceabilityPageSize, setTraceabilityPageSize] = useState(5);
  const [loadingTraceability, setLoadingTraceability] = useState(false);
  const [traceabilityActionFilter, setTraceabilityActionFilter] =
    useState<TraceabilityActionValue>("");
  const [traceabilityStatusFilter, setTraceabilityStatusFilter] =
    useState<TraceabilityStatusValue>("");
  const [traceabilityDateFrom, setTraceabilityDateFrom] = useState("");
  const [traceabilityDateTo, setTraceabilityDateTo] = useState("");

  const isCampaignDisabled = !campaignType;
  const isSourceDisabled = !campaignType;

  const {
    hasCandidates,
    totalCandidates,
    loading: loadingAvailableCandidates,
  } = useAvailableTicketCandidates({
    campaignType,
    dataSource,
  });

  const hasInternalSearchCandidates =
    campaignType === "MIGRATION"
      ? migrationSearchCandidates.length > 0
      : campaignType === "FIDELIZATION"
        ? fidelizationSearchCandidates.length > 0
        : false;

  const canCreateTickets =
    dataSource === "INTERNAL_SEARCH"
      ? !!campaignType && hasInternalSearchCandidates
      : !!campaignType &&
        !!dataSource &&
        hasCandidates &&
        !loadingAvailableCandidates;

  useEffect(() => {
    setDataSource("");
    setMigrationSearchCandidates([]);
    setMigrationReportFilters(null);
    setFidelizationReportFilters(null);
    setFidelizationSearchCandidates([]);
    setTraceabilityPage(0);
    setOpenMigrationTicketsModal(false);
    setOpenFidelizationTicketsModal(false);
    setOpenParameterDrawer(false);
    setOpenNotFoundModal(false);
    setTraceabilityActionFilter("");
    setTraceabilityStatusFilter("");
    setTraceabilityDateFrom("");
    setTraceabilityDateTo("");
  }, [campaignType]);

  const handleOpenTicketsModal = () => {
    if (campaignType === "MIGRATION") {
      setOpenMigrationTicketsModal(true);
    }

    if (campaignType === "FIDELIZATION") {
      setOpenFidelizationTicketsModal(true);
    }
  };

  const handleChangeDataSource = (value: DataSourceType) => {
    setDataSource(value);
    setMigrationSearchCandidates([]);
    setMigrationReportFilters(null);
    setFidelizationReportFilters(null);
    setFidelizationSearchCandidates([]);
    setTraceabilityPage(0);
    setOpenMigrationTicketsModal(false);
    setOpenFidelizationTicketsModal(false);
    setOpenNotFoundModal(false);
    setTraceabilityActionFilter("");
    setTraceabilityStatusFilter("");
    setTraceabilityDateFrom("");
    setTraceabilityDateTo("");
  };

  const handleCloseParameterDrawer = () => {
    setOpenParameterDrawer(false);
  };

  const loadTraceability = useCallback(
    async (page = traceabilityPage, size = traceabilityPageSize) => {
      if (!campaignType || !dataSource) {
        setTraceabilityRows([]);
        setTraceabilityTotal(0);
        return;
      }

      try {
        setLoadingTraceability(true);

        const response = await getCampaignTraceability({
          module: campaignType,
          dataSource,
          action: traceabilityActionFilter || undefined,
          status: traceabilityStatusFilter || undefined,
          desde: traceabilityDateFrom || undefined,
          hasta: traceabilityDateTo || undefined,
          page,
          size,
        });

        const rows = response.content ?? [];
        const totalElements =
          response.totalElements ??
          (response as any)?.page?.totalElements ??
          rows.length;

        setTraceabilityRows(rows);
        setTraceabilityTotal(totalElements);
      } catch {
        setTraceabilityRows([]);
        setTraceabilityTotal(0);
      } finally {
        setLoadingTraceability(false);
      }
    },
    [
      campaignType,
      dataSource,
      traceabilityActionFilter,
      traceabilityDateFrom,
      traceabilityDateTo,
      traceabilityPage,
      traceabilityPageSize,
      traceabilityStatusFilter,
    ],
  );

  useEffect(() => {
    if (dataSource !== "INTERNAL_SEARCH") {
      setTraceabilityRows([]);
      setTraceabilityTotal(0);
      return;
    }

    loadTraceability();
  }, [dataSource, loadTraceability]);

  const parameterTitle =
    campaignType === "MIGRATION"
      ? "Parametrización de Migración"
      : campaignType === "FIDELIZATION"
        ? "Parametrización de Fidelización"
        : "Parametrización";

  const handleClearTraceabilityFilters = () => {
    setTraceabilityActionFilter("");
    setTraceabilityStatusFilter("");
    setTraceabilityDateFrom("");
    setTraceabilityDateTo("");
    setTraceabilityPage(0);
  };

  const traceabilityColumns = useMemo<
    GridColDef<CampaignTraceabilityResponse>[]
  >(
    () => [
      {
        field: "createdAt",
        headerName: "Fecha",
        minWidth: 170,
        flex: 0.9,
        valueGetter: (params) => formatTraceabilityDate(params.row.createdAt),
      },
      {
        field: "module",
        headerName: "Campaña",
        minWidth: 130,
        flex: 0.7,
        valueGetter: (params) =>
          params.row.module === "MIGRATION" ? "Migración" : "Fidelización",
      },
      {
        field: "username",
        headerName: "Realizado por el usuario",
        minWidth: 210,
        flex: 1,
        valueGetter: (params) =>
          params.row.username || params.row.userId || "-",
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            variant="outlined"
            sx={{ maxWidth: "100%", fontWeight: 600 }}
          />
        ),
      },
      {
        field: "customerName",
        headerName: "Razón social cliente",
        minWidth: 230,
        flex: 1.2,
        valueGetter: (params) => summarizeCustomers(params.row, "name"),
        renderCell: (params) => (
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            title={String(params.value ?? "")}
            sx={{ maxWidth: "100%" }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "action",
        headerName: "Acción",
        minWidth: 150,
        flex: 0.8,
        renderCell: (params) => (
          <Chip
            size="small"
            label={
              traceabilityActionLabels[params.row.action] ?? params.row.action
            }
            color={traceabilityActionColors[params.row.action] ?? "default"}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        ),
      },
      {
        field: "message",
        headerName: "Detalle",
        minWidth: 260,
        flex: 1.4,
        valueGetter: (params) => params.row.message ?? "-",
        renderCell: (params) => (
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            title={String(params.value ?? "")}
            sx={{ maxWidth: "100%" }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "totalFound",
        headerName: "Encontrados",
        type: "number",
        minWidth: 120,
        flex: 0.6,
        valueGetter: (params) => params.row.totalFound ?? "-",
      },
      {
        field: "candidatesSaved",
        headerName: "Guardados",
        type: "number",
        minWidth: 110,
        flex: 0.6,
        valueGetter: (params) => params.row.candidatesSaved ?? "-",
      },
      {
        field: "selectedCandidates",
        headerName: "Seleccionados",
        type: "number",
        minWidth: 130,
        flex: 0.6,
        valueGetter: (params) => countJsonLikeList(params.row.selectedCandidates),
      },
      {
        field: "ticketsCreated",
        headerName: "Tickets OK",
        type: "number",
        minWidth: 110,
        flex: 0.6,
        valueGetter: (params) => params.row.ticketsCreated ?? "-",
      },
      {
        field: "ticketsFailed",
        headerName: "Tickets error",
        type: "number",
        minWidth: 120,
        flex: 0.6,
        valueGetter: (params) => params.row.ticketsFailed ?? "-",
      },
      {
        field: "emailRecipients",
        headerName: "Correos",
        type: "number",
        minWidth: 100,
        flex: 0.5,
        valueGetter: (params) => countJsonLikeList(params.row.emailRecipients),
      },
      {
        field: "status",
        headerName: "Estado",
        minWidth: 120,
        flex: 0.6,
        renderCell: (params) => (
          <Chip
            size="small"
            color={traceabilityStatusColors[params.row.status] ?? "default"}
            label={
              traceabilityStatusLabels[params.row.status] ?? params.row.status
            }
            sx={{ fontWeight: 700 }}
          />
        ),
      },
    ],
    [],
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "grey.50", minHeight: "100%" }}>
      <Stack spacing={3}>
        <Card
          sx={{
            borderRadius: 1,
            boxShadow: 0,
            border: "1px solid",
            borderColor: "divider",
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2.5}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    bgcolor: "grey.900",
                    color: "primary.contrastText",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <CampaignOutlinedIcon />
                </Box>

                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography variant="h4" fontWeight={800}>
                      Campañas
                    </Typography>

                    {campaignType && (
                      <Chip
                        size="small"
                        label={getCampaignLabel(campaignType)}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                      />
                    )}

                    {dataSource && (
                      <Chip
                        size="small"
                        label={getDataSourceLabel(dataSource)}
                        color="info"
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  </Stack>

                  {(!campaignType || !dataSource) && (
                    <Typography variant="body1" color="text.secondary" mt={0.5}>
                      {campaignType
                        ? "Elige la fuente de datos para continuar."
                        : "Selecciona el tipo de campaña para continuar."}
                    </Typography>
                  )}
                </Box>
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Tipo de campaña</InputLabel>
                  <Select
                    value={campaignType}
                    label="Tipo de campaña"
                    onChange={(e) =>
                      setCampaignType(e.target.value as CampaignTypeValue)
                    }
                  >
                    <MenuItem value="">Seleccionar</MenuItem>
                    <MenuItem value="MIGRATION">Migración</MenuItem>
                    <MenuItem value="FIDELIZATION">Fidelización</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 220 }} disabled={isSourceDisabled}>
                  <InputLabel>Fuente de datos</InputLabel>
                  <Select
                    value={dataSource}
                    label="Fuente de datos"
                    onChange={(e) =>
                      handleChangeDataSource(e.target.value as DataSourceType)
                    }
                  >
                    <MenuItem value="">Seleccionar</MenuItem>
                    <MenuItem value="CSV">Carga CSV/XLSX</MenuItem>

                    <MenuItem value="INTERNAL_SEARCH">
                      Búsqueda interna
                    </MenuItem>
                  </Select>
                </FormControl>

                {dataSource !== "INTERNAL_SEARCH" && (
                  <Button
                    variant="outlined"
                    disabled={isCampaignDisabled}
                    onClick={() => setOpenParameterDrawer(true)}
                    startIcon={<TuneOutlinedIcon />}
                  >
                    Parametrización
                  </Button>
                )}

                <Button
                  variant="contained"
                  disabled={!canCreateTickets}
                  onClick={handleOpenTicketsModal}
                  startIcon={<ConfirmationNumberOutlinedIcon />}
                >
                  Crear tickets
                </Button>

                {dataSource !== "INTERNAL_SEARCH" && (
                  <Button
                    variant="outlined"
                    disabled={!campaignType || !dataSource}
                    onClick={() => setOpenNotFoundModal(true)}
                    startIcon={<ReportProblemOutlinedIcon />}
                  >
                    No encontrados
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {campaignType &&
          dataSource &&
          !loadingAvailableCandidates &&
          hasCandidates && (
            <Box>
              <Chip
                icon={<ConfirmationNumberOutlinedIcon />}
                label={`Candidatos disponibles para crear tickets: ${totalCandidates}`}
                color="success"
                variant="outlined"
                sx={{ bgcolor: "background.paper", fontWeight: 700 }}
              />
            </Box>
          )}

        {!campaignType ? (
          <Card
            sx={{
              borderRadius: 1,
              boxShadow: 0,
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ py: 8 }}>
              <Stack spacing={2} alignItems="center" justifyContent="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    color: "primary.main",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <CampaignOutlinedIcon fontSize="large" />
                </Box>

                <Typography variant="h5" fontWeight={700}>
                  Selecciona un tipo de campaña
                </Typography>

                <Typography variant="body1" color="text.secondary">
                  Debes elegir Migración o Fidelización para continuar.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : !dataSource ? (
          <Card
            sx={{
              borderRadius: 1,
              boxShadow: 0,
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ py: 8 }}>
              <Stack spacing={2} alignItems="center" justifyContent="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    color: "info.main",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <ManageSearchOutlinedIcon fontSize="large" />
                </Box>

                <Typography variant="h5" fontWeight={700}>
                  Selecciona una fuente de datos
                </Typography>

                <Typography variant="body1" color="text.secondary">
                  Puedes cargar un archivo CSV/XLSX o realizar una búsqueda
                  interna.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : dataSource === "CSV" ? (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <SaveMigracion campaignType={campaignType} />
              </Grid>

              <Grid item xs={12} md={4}>
                <ConfigSummaryCard campaignType={campaignType} />
              </Grid>
            </Grid>

            <Box mt={3}>
              <TableMigracion campaignType={campaignType} />
            </Box>
          </>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <InternalSearchCandidates
                  campaignType={campaignType}
                  onSearchSuccess={() => {
                    setTraceabilityPage(0);
                    loadTraceability(0, traceabilityPageSize);
                  }}
                  onFidelizationFiltersChange={setFidelizationReportFilters}
                  onFidelizationResultsChange={setFidelizationSearchCandidates}
                  onMigrationFiltersChange={setMigrationReportFilters}
                  onMigrationResultsChange={setMigrationSearchCandidates}
                />
              </Grid>
            </Grid>

            <Box mt={3}>
              <Card
                sx={{
                  borderRadius: 1,
                  boxShadow: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: "10px 18px auto 18px",
                    height: 26,
                    borderRadius: "50%",
                    bgcolor: "rgba(15, 23, 42, 0.18)",
                    filter: "blur(22px)",
                    pointerEvents: "none",
                  },
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1.5}
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: 2,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      color: "text.primary",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: "grey.900",
                          color: "common.white",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <HistoryOutlinedIcon />
                      </Box>

                      <Box>
                        <Typography variant="h6" fontWeight={800}>
                          Trazabilidad
                        </Typography>

                        <Typography
                          variant="body2"
                          mt={0.25}
                          color="text.secondary"
                        >
                          Historial de búsquedas, tickets creados y correos
                          enviados.
                        </Typography>
                      </Box>
                    </Stack>

                    <Chip
                      size="small"
                      label={`${traceabilityTotal} evento(s)`}
                      variant="outlined"
                      sx={{
                        borderColor: "grey.300",
                        color: "text.primary",
                        fontWeight: 700,
                        bgcolor: "grey.50",
                      }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: 2,
                      bgcolor: "background.paper",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "stretch", md: "center" }}
                    >
                      <Chip
                        icon={<FilterListOutlinedIcon />}
                        label="Filtros"
                        variant="outlined"
                        sx={{
                          alignSelf: { xs: "flex-start", md: "center" },
                          fontWeight: 800,
                        }}
                      />

                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Acción</InputLabel>
                        <Select
                          value={traceabilityActionFilter}
                          label="Acción"
                          onChange={(event) => {
                            setTraceabilityPage(0);
                            setTraceabilityActionFilter(
                              event.target.value as TraceabilityActionValue,
                            );
                          }}
                        >
                          <MenuItem value="">Todas</MenuItem>
                          <MenuItem value="SEARCH">Búsqueda</MenuItem>
                          <MenuItem value="CREATE_TICKETS">
                            Crear tickets
                          </MenuItem>
                          <MenuItem value="SEND_EMAIL">Enviar email</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Estado</InputLabel>
                        <Select
                          value={traceabilityStatusFilter}
                          label="Estado"
                          onChange={(event) => {
                            setTraceabilityPage(0);
                            setTraceabilityStatusFilter(
                              event.target.value as TraceabilityStatusValue,
                            );
                          }}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          <MenuItem value="SUCCESS">Correcto</MenuItem>
                          <MenuItem value="WARNING">Parcial</MenuItem>
                          <MenuItem value="ERROR">Error</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        size="small"
                        label="Desde"
                        type="date"
                        value={traceabilityDateFrom}
                        onChange={(event) => {
                          setTraceabilityPage(0);
                          setTraceabilityDateFrom(event.target.value);
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 160 }}
                      />

                      <TextField
                        size="small"
                        label="Hasta"
                        type="date"
                        value={traceabilityDateTo}
                        onChange={(event) => {
                          setTraceabilityPage(0);
                          setTraceabilityDateTo(event.target.value);
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 160 }}
                      />

                      <Box sx={{ flex: 1 }} />

                      <Button
                        variant="outlined"
                        startIcon={<ClearOutlinedIcon />}
                        onClick={handleClearTraceabilityFilters}
                        disabled={
                          !traceabilityActionFilter &&
                          !traceabilityStatusFilter &&
                          !traceabilityDateFrom &&
                          !traceabilityDateTo
                        }
                      >
                        Limpiar
                      </Button>
                    </Stack>
                  </Box>

                  <Box sx={{ height: 440, width: "100%" }}>
                    <DataGrid
                      rows={traceabilityRows}
                      columns={traceabilityColumns}
                      getRowId={(row) => row.id}
                      loading={loadingTraceability}
                      disableRowSelectionOnClick
                      disableColumnSelector
                      paginationMode="server"
                      rowHeight={54}
                      columnHeaderHeight={44}
                      rowCount={traceabilityTotal}
                      paginationModel={{
                        page: traceabilityPage,
                        pageSize: traceabilityPageSize,
                      }}
                      onPaginationModelChange={(model) => {
                        setTraceabilityPage(model.page);
                        setTraceabilityPageSize(model.pageSize);
                      }}
                      pageSizeOptions={[5, 10, 20]}
                      localeText={{
                        noRowsLabel: "Aún no hay eventos de trazabilidad",
                        columnMenuSortAsc: "Ordenar ascendente",
                        columnMenuSortDesc: "Ordenar descendente",
                        columnMenuFilter: "Filtrar",
                        columnMenuHideColumn: "Ocultar columna",
                        columnMenuManageColumns: "Administrar columnas",
                        columnMenuShowColumns: "Mostrar columnas",
                        columnMenuUnsort: "Quitar orden",
                        columnHeaderSortIconLabel: "Ordenar",
                        filterPanelAddFilter: "Agregar filtro",
                        filterPanelRemoveAll: "Quitar todos",
                        filterPanelDeleteIconLabel: "Eliminar",
                        filterPanelLogicOperator: "Operador lógico",
                        filterPanelOperator: "Operador",
                        filterPanelOperatorAnd: "Y",
                        filterPanelOperatorOr: "O",
                        filterPanelColumns: "Columnas",
                        filterPanelInputLabel: "Valor",
                        filterPanelInputPlaceholder: "Valor a filtrar",
                        filterOperatorContains: "contiene",
                        filterOperatorEquals: "es igual a",
                        filterOperatorStartsWith: "empieza con",
                        filterOperatorEndsWith: "termina con",
                        filterOperatorIs: "es",
                        filterOperatorNot: "no es",
                        filterOperatorAfter: "es posterior a",
                        filterOperatorOnOrAfter: "es en o posterior a",
                        filterOperatorBefore: "es anterior a",
                        filterOperatorOnOrBefore: "es en o anterior a",
                        filterOperatorIsEmpty: "está vacío",
                        filterOperatorIsNotEmpty: "no está vacío",
                        filterOperatorIsAnyOf: "es cualquiera de",
                        footerRowSelected: (count) =>
                          `${count.toLocaleString()} fila(s) seleccionada(s)`,
                        MuiTablePagination: {
                          labelRowsPerPage: "Filas por página",
                          labelDisplayedRows: ({ from, to, count }) =>
                            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`,
                        },
                      }}
                      sx={{
                        border: 0,
                        "& .MuiDataGrid-columnHeaders": {
                          bgcolor: "grey.100",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: 800,
                        },
                        "& .MuiDataGrid-cell": {
                          outline: "none !important",
                          borderColor: "divider",
                        },
                        "& .MuiDataGrid-row:hover": {
                          bgcolor: "action.hover",
                        },
                        "& .MuiDataGrid-footerContainer": {
                          borderTop: "1px solid",
                          borderColor: "divider",
                          bgcolor: "grey.50",
                        },
                        "& .MuiDataGrid-columnSeparator": {
                          color: "divider",
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </Stack>

      <Drawer
        anchor="right"
        open={openParameterDrawer}
        onClose={handleCloseParameterDrawer}
      >
        <Box sx={{ width: 420 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="h5" fontWeight={700}>
              {parameterTitle}
            </Typography>

            <IconButton
              onClick={handleCloseParameterDrawer}
              aria-label="Cerrar parametrización"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{ p: 3 }}>
            {campaignType === "MIGRATION" ? (
              <ParameterFormMigracion onSuccess={handleCloseParameterDrawer} />
            ) : campaignType === "FIDELIZATION" ? (
              <ParameterFormFidelizacion
                onSuccess={handleCloseParameterDrawer}
              />
            ) : null}
          </Box>
        </Box>
      </Drawer>

      <TicketsModalMigracion
        open={openMigrationTicketsModal}
        onClose={() => setOpenMigrationTicketsModal(false)}
        dataSource={dataSource}
        reportFilters={migrationReportFilters}
        internalSearchCandidates={migrationSearchCandidates}
        onTraceabilityRefresh={
          dataSource === "INTERNAL_SEARCH"
            ? () => loadTraceability(0, traceabilityPageSize)
            : undefined
        }
      />

      <TicketsModalFidelizacion
        open={openFidelizationTicketsModal}
        onClose={() => setOpenFidelizationTicketsModal(false)}
        dataSource={dataSource}
        reportFilters={fidelizationReportFilters}
        internalSearchCandidates={fidelizationSearchCandidates}
        onTraceabilityRefresh={
          dataSource === "INTERNAL_SEARCH"
            ? () => loadTraceability(0, traceabilityPageSize)
            : undefined
        }
      />

      {campaignType && dataSource && (
        <NotFoundCandidatesModal
          open={openNotFoundModal}
          onClose={() => setOpenNotFoundModal(false)}
          module={campaignType}
          dataSource={dataSource}
        />
      )}
    </Box>
  );
};
